import React, { useEffect, useState } from 'react';
import {
  RefreshCw,
  Search,
  Plus,
  Truck,
  MapPin,
  Phone,
  Building2,
  AlertCircle,
  CheckCircle2,
  Filter,
  XCircle,
  Loader2,
} from 'lucide-react';

const API_URL = '/api';

function DisponibilidadeChip({ value }) {
  if (value === 'disponivel') return <span className="chip-success">Disponível</span>;
  if (value === 'ocupado') return <span className="chip-warning">Ocupado</span>;
  return <span className="chip-neutral">Indisponível</span>;
}

export default function Prestadores({ apiFetch, podeGerenciarPrestadores }) {
  const [prestadores, setPrestadores] = useState([]);
  const [carregandoPrestadores, setCarregandoPrestadores] = useState(true);
  const [erroPrestadores, setErroPrestadores] = useState(null);
  const [mensagemPrestadores, setMensagemPrestadores] = useState(null);

  const [busca, setBusca] = useState('');
  const [regiao, setRegiao] = useState('');
  const [tipo, setTipo] = useState('');

  const [razaoSocial, setRazaoSocial] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [telefonePrincipal, setTelefonePrincipal] = useState('');
  const [telefoneSecundario, setTelefoneSecundario] = useState('');
  const [endereco, setEndereco] = useState('');
  const [cidade, setCidade] = useState('');
  const [estado, setEstado] = useState('CE');
  const [regiaoAtuacao, setRegiaoAtuacao] = useState('');
  const [tiposServicoTexto, setTiposServicoTexto] = useState('reboque');
  const [disponibilidade, setDisponibilidade] = useState('disponivel');
  const [criandoPrestador, setCriandoPrestador] = useState(false);

  const carregarPrestadores = async () => {
    try {
      setCarregandoPrestadores(true);
      setErroPrestadores(null);

      const params = new URLSearchParams();
      if (busca.trim()) params.set('busca', busca.trim());
      if (regiao.trim()) params.set('regiao', regiao.trim());
      if (tipo.trim()) params.set('tipo', tipo.trim());

      const query = params.toString();
      const response = await apiFetch(`${API_URL}/prestadores${query ? `?${query}` : ''}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao carregar prestadores');
      }

      setPrestadores(Array.isArray(data) ? data : []);
    } catch (err) {
      setErroPrestadores(err.message || 'Erro ao carregar prestadores');
    } finally {
      setCarregandoPrestadores(false);
    }
  };

  const criarPrestador = async (e) => {
    e.preventDefault();

    try {
      setCriandoPrestador(true);
      setErroPrestadores(null);
      setMensagemPrestadores(null);

      const tiposServico = tiposServicoTexto
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

      const response = await apiFetch(`${API_URL}/prestadores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          razaoSocial,
          nomeFantasia: nomeFantasia || null,
          cnpj,
          telefonePrincipal,
          telefoneSecundario: telefoneSecundario || null,
          endereco,
          cidade,
          estado,
          regiaoAtuacao,
          tiposServico,
          disponibilidade,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao criar prestador');
      }

      setMensagemPrestadores('Prestador cadastrado com sucesso.');
      setRazaoSocial('');
      setNomeFantasia('');
      setCnpj('');
      setTelefonePrincipal('');
      setTelefoneSecundario('');
      setEndereco('');
      setCidade('');
      setEstado('CE');
      setRegiaoAtuacao('');
      setTiposServicoTexto('reboque');
      setDisponibilidade('disponivel');

      await carregarPrestadores();
    } catch (err) {
      setErroPrestadores(err.message || 'Erro ao criar prestador');
    } finally {
      setCriandoPrestador(false);
    }
  };

  useEffect(() => {
    carregarPrestadores();
  }, []);

  const limparFiltros = () => {
    setBusca('');
    setRegiao('');
    setTipo('');
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-ink tracking-tight">Prestadores</h1>
          <p className="text-sm text-ink-subtle mt-1">
            Base de reboques e parceiros para atendimento operacional.
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={carregarPrestadores}
          disabled={carregandoPrestadores}
        >
          {carregandoPrestadores ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
          {carregandoPrestadores ? 'Atualizando…' : 'Atualizar'}
        </button>
      </div>

      {erroPrestadores && (
        <div className="flex items-start gap-2 rounded-xl bg-danger-50 text-danger-700 px-3.5 py-2.5 text-sm">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{erroPrestadores}</span>
        </div>
      )}
      {mensagemPrestadores && (
        <div className="flex items-start gap-2 rounded-xl bg-success-50 text-success-700 px-3.5 py-2.5 text-sm">
          <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
          <span>{mensagemPrestadores}</span>
        </div>
      )}

      <section className="card p-5">
        <header className="flex items-center gap-2 mb-4">
          <Filter size={16} className="text-ink-muted" />
          <h2 className="text-sm font-semibold text-ink">Filtros</h2>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" />
            <input
              type="text"
              placeholder="Razão social ou nome fantasia"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="input pl-9"
            />
          </div>
          <input
            type="text"
            placeholder="Região"
            value={regiao}
            onChange={(e) => setRegiao(e.target.value)}
            className="input"
          />
          <input
            type="text"
            placeholder="Tipo de serviço"
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            className="input"
          />
        </div>

        <div className="flex items-center gap-2 mt-4">
          <button type="button" className="btn-primary" onClick={carregarPrestadores}>
            <Search size={16} /> Aplicar
          </button>
          <button type="button" className="btn-ghost" onClick={limparFiltros}>
            <XCircle size={16} /> Limpar
          </button>
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <header className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-ink">Prestadores cadastrados</h2>
          <span className="text-xs text-ink-subtle">
            {carregandoPrestadores ? '…' : `${prestadores.length} encontrado(s)`}
          </span>
        </header>

        {carregandoPrestadores ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="card p-5 animate-pulse">
                <div className="h-4 bg-surface-subtle rounded w-2/3 mb-3" />
                <div className="h-3 bg-surface-subtle rounded w-1/3 mb-5" />
                <div className="grid grid-cols-2 gap-3">
                  <div className="h-9 bg-surface-subtle rounded" />
                  <div className="h-9 bg-surface-subtle rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : prestadores.length === 0 ? (
          <div className="card p-8 flex flex-col items-center text-center">
            <Truck size={28} className="text-ink-faint mb-2" />
            <p className="text-sm text-ink-muted">Nenhum prestador encontrado.</p>
            <p className="text-xs text-ink-faint mt-1">Ajuste os filtros ou cadastre um novo abaixo.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {prestadores.map((p) => (
              <article key={p.id} className="card p-5 flex flex-col gap-4 transition-shadow hover:shadow-pop">
                <header className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-ink truncate">{p.razaoSocial}</h3>
                    {p.nomeFantasia && (
                      <p className="text-xs text-ink-subtle truncate mt-0.5">{p.nomeFantasia}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <span className="chip-brand">{p.regiaoAtuacao}</span>
                    <DisponibilidadeChip value={p.disponibilidade} />
                  </div>
                </header>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex flex-col">
                    <span className="text-[11px] uppercase tracking-wider text-ink-faint">CNPJ</span>
                    <span className="text-ink font-medium truncate">{p.cnpj}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[11px] uppercase tracking-wider text-ink-faint">Telefone</span>
                    <span className="text-ink font-medium truncate">{p.telefonePrincipal}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[11px] uppercase tracking-wider text-ink-faint">Cidade/UF</span>
                    <span className="text-ink font-medium truncate">{p.cidade}/{p.estado}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[11px] uppercase tracking-wider text-ink-faint">Serviços</span>
                    <span className="text-ink font-medium truncate">
                      {Array.isArray(p.tiposServico) ? p.tiposServico.join(', ') : '—'}
                    </span>
                  </div>
                </div>

                {p.endereco && (
                  <footer className="flex items-start gap-2 text-xs text-ink-subtle border-t border-line/60 pt-3">
                    <MapPin size={13} className="mt-0.5 shrink-0" />
                    <span className="truncate">{p.endereco}</span>
                  </footer>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="card p-5">
        <header className="flex items-center gap-2 mb-1">
          <Building2 size={16} className="text-ink-muted" />
          <h2 className="text-sm font-semibold text-ink">Novo prestador</h2>
        </header>
        <p className="text-xs text-ink-subtle mb-4">Cadastre um parceiro para a base operacional.</p>

        {!podeGerenciarPrestadores && (
          <div className="mb-4 flex items-start gap-2 rounded-xl bg-warning-50 text-warning-700 px-3.5 py-2.5 text-sm">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>Seu perfil não tem permissão para cadastrar prestadores.</span>
          </div>
        )}

        <form onSubmit={criarPrestador} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-2">
              <label className="label">Razão social *</label>
              <input
                type="text"
                value={razaoSocial}
                onChange={(e) => setRazaoSocial(e.target.value)}
                className="input"
                required
                disabled={!podeGerenciarPrestadores}
              />
            </div>
            <div>
              <label className="label">Nome fantasia</label>
              <input
                type="text"
                value={nomeFantasia}
                onChange={(e) => setNomeFantasia(e.target.value)}
                className="input"
                disabled={!podeGerenciarPrestadores}
              />
            </div>
            <div>
              <label className="label">CNPJ *</label>
              <input
                type="text"
                value={cnpj}
                onChange={(e) => setCnpj(e.target.value)}
                className="input"
                required
                disabled={!podeGerenciarPrestadores}
              />
            </div>
            <div>
              <label className="label">Telefone principal *</label>
              <div className="relative">
                <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" />
                <input
                  type="text"
                  value={telefonePrincipal}
                  onChange={(e) => setTelefonePrincipal(e.target.value)}
                  className="input pl-9"
                  required
                  disabled={!podeGerenciarPrestadores}
                />
              </div>
            </div>
            <div>
              <label className="label">Telefone secundário</label>
              <div className="relative">
                <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" />
                <input
                  type="text"
                  value={telefoneSecundario}
                  onChange={(e) => setTelefoneSecundario(e.target.value)}
                  className="input pl-9"
                  disabled={!podeGerenciarPrestadores}
                />
              </div>
            </div>
            <div className="md:col-span-2 lg:col-span-2">
              <label className="label">Endereço *</label>
              <input
                type="text"
                value={endereco}
                onChange={(e) => setEndereco(e.target.value)}
                className="input"
                required
                disabled={!podeGerenciarPrestadores}
              />
            </div>
            <div>
              <label className="label">Cidade *</label>
              <input
                type="text"
                value={cidade}
                onChange={(e) => setCidade(e.target.value)}
                className="input"
                required
                disabled={!podeGerenciarPrestadores}
              />
            </div>
            <div>
              <label className="label">UF *</label>
              <input
                type="text"
                maxLength={2}
                value={estado}
                onChange={(e) => setEstado(e.target.value.toUpperCase())}
                className="input"
                required
                disabled={!podeGerenciarPrestadores}
              />
            </div>
            <div>
              <label className="label">Região de atuação *</label>
              <input
                type="text"
                value={regiaoAtuacao}
                onChange={(e) => setRegiaoAtuacao(e.target.value)}
                className="input"
                required
                disabled={!podeGerenciarPrestadores}
              />
            </div>
            <div>
              <label className="label">Tipos de serviço *</label>
              <input
                type="text"
                placeholder="reboque, pane, …"
                value={tiposServicoTexto}
                onChange={(e) => setTiposServicoTexto(e.target.value)}
                className="input"
                required
                disabled={!podeGerenciarPrestadores}
              />
            </div>
            <div>
              <label className="label">Disponibilidade</label>
              <select
                value={disponibilidade}
                onChange={(e) => setDisponibilidade(e.target.value)}
                className="input"
                disabled={!podeGerenciarPrestadores}
              >
                <option value="disponivel">Disponível</option>
                <option value="ocupado">Ocupado</option>
                <option value="indisponivel">Indisponível</option>
              </select>
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="btn-primary"
              disabled={!podeGerenciarPrestadores || criandoPrestador}
            >
              {criandoPrestador ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              {criandoPrestador ? 'Salvando…' : 'Cadastrar prestador'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
